"use client";

// @ubm/product-ui/blocks/availability-picker — HOUSE IMPLEMENTATION from the public UIArc specification
// (https://uiarc.dev/components/blocks/availability-picker/markdown) ; original Pro implementation not received.
// Composition : Avatar, Button canons (@ubm/ui/components/*), Motion (glissement selon le sens du changement de jour,
// surbrillance partagée par layoutId).
// Adaptations maison déclarées : jours, créneaux (INSTANTS stables calculés en amont), participants, fuseau affiché, titre et
// durée sont INJECTÉS ; aucun calcul de disponibilité ni fuseau codé ici. La source publique remonte `{ date, time }` en
// textes : l'API maison remonte l'IDENTITÉ et les INSTANTS de la sélection (`{ dayId, slotId, start, end }`). `onConfirm`
// est un port Promise : attente, refus conservé avec « Réessayer », réussite annoncée ; sans port, confirmer est indisponible.
import { useEffect, useLayoutEffect, useId, useRef, useState, type CSSProperties } from "react";
import { AnimatePresence, LayoutGroup, motion, useReducedMotionConfig as useReducedMotion } from "motion/react";
import { CalendarClock, Check } from "lucide-react";
import { Avatar } from "@ubm/ui/components/avatar";
import { Button } from "@ubm/ui/components/button";
import { motionTokens } from "@ubm/ui/motion-tokens";
import { PresenceDiv, useContainerWidth } from "../_conversation";
import styles from "./availability-picker.module.css";

export interface AvailabilitySlot {
  id: string;
  /** Stable instant (ISO 8601 with offset, or a Date) computed upstream. */
  start: string | Date;
  end?: string | Date;
  /** Display label in the shown zone ("10:30"). */
  label: string;
  /** Someone is unavailable: shown, never selectable. */
  busy?: boolean;
}
export interface AvailabilityDay {
  id: string;
  /** Short weekday ("Thu"). */
  weekday: string;
  /** Day number or short date ("24"). */
  day: string;
  /** Full accessible label ("Thursday, September 24"). */
  label: string;
  slots: AvailabilitySlot[];
}
export interface AvailabilityAttendee { name: string; avatar?: string; role?: string }
export interface AvailabilitySelection { dayId: string; slotId: string }
export interface AvailabilityConfirmation extends AvailabilitySelection { start: string | Date; end?: string | Date }
export interface AvailabilityPickerStrings {
  days?: string;
  times?: (day: string) => string;
  busy?: string;
  none?: string;
  choose?: string;
  selected?: (day: string, time: string) => string;
  confirm?: string;
  confirming?: string;
  confirmed?: (day: string, time: string) => string;
  failed?: string;
  retry?: string;
  unavailable?: string;
  taken?: string;
  attendees?: string;
}
export interface AvailabilityPickerProps {
  title: string;
  /** "30 min", "1 hour"… shown under the title. */
  duration?: string;
  /** "Times in Zürich (CEST)" — the zone the labels are written in. */
  timeZoneLabel?: string;
  attendees?: AvailabilityAttendee[];
  days: AvailabilityDay[];
  value?: AvailabilitySelection | null;
  defaultValue?: AvailabilitySelection | null;
  onValueChange?: (selection: AvailabilitySelection | null) => void;
  /** Books the selection. Awaited; a rejection keeps the choice and offers Retry. Without it, confirming is unavailable. */
  onConfirm?: (selection: AvailabilityConfirmation) => void | Promise<void>;
  strings?: AvailabilityPickerStrings;
  className?: string;
  style?: CSSProperties;
}

const STRINGS: Required<AvailabilityPickerStrings> = {
  days: "Days",
  times: day => `Times on ${day}`,
  busy: "busy",
  none: "No times left on this day.",
  choose: "Choose a time",
  selected: (day, time) => `${day} at ${time}`,
  confirm: "Confirm time",
  confirming: "Confirming…",
  confirmed: (day, time) => `Booked for ${day} at ${time}.`,
  failed: "That time couldn't be booked. Your choice is kept.",
  retry: "Retry",
  unavailable: "Booking isn't available here.",
  taken: "That time is no longer available. Choose another one.",
  attendees: "Attendees",
};
const NARROW = 700, TINY = 490;

export function AvailabilityPicker({ title, duration, timeZoneLabel, attendees = [], days, value: valueProp, defaultValue = null, onValueChange, onConfirm, strings, className, style }: AvailabilityPickerProps) {
  const t = { ...STRINGS, ...strings };
  const reduced = !!useReducedMotion();
  const uid = useId();
  const root = useRef<HTMLElement>(null);
  const width = useContainerWidth(root, 1024);
  const narrow = width > 0 && width < NARROW;
  const tiny = width > 0 && width < TINY;
  const [inner, setInner] = useState<AvailabilitySelection | null>(defaultValue);
  const value = valueProp !== undefined ? valueProp : inner;
  const [dayId, setDayId] = useState(value?.dayId ?? days[0]?.id ?? "");
  const [direction, setDirection] = useState(1);
  const [pending, setPending] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; message: string } | null>(null);
  const inFlight = useRef(false);
  const operation = useRef(0);
  useEffect(() => () => { operation.current++; }, []);
  useEffect(() => { if (!days.some(item => item.id === dayId)) setDayId(days[0]?.id ?? ""); }, [days, dayId]);

  const dayIndex = Math.max(0, days.findIndex(d => d.id === dayId));
  const day = days[dayIndex];
  const chosenDay = value ? days.find(d => d.id === value.dayId) : undefined;
  const chosenSlot = chosenDay?.slots.find(s => s.id === value?.slotId);
  const selectionKey = [value?.dayId ?? "", value?.slotId ?? "", chosenSlot ? new Date(chosenSlot.start).getTime() : "", chosenSlot?.end ? new Date(chosenSlot.end).getTime() : "", chosenSlot?.busy ?? false].join("|");
  // A reply belongs to the exact selected slot/version, never to a newer parent selection.
  useLayoutEffect(() => {
    operation.current++;
    inFlight.current = false;
    setPending(false);
    setResult(null);
    return () => { operation.current++; };
  }, [selectionKey]);
  const confirmed = result?.ok === true;
  /** The kept selection became busy (parent update): it stays shown but can no longer be confirmed or retried. */
  const taken = !!chosenSlot?.busy;

  const select = (next: AvailabilitySelection | null) => {
    if (pending || confirmed) return;
    if (valueProp === undefined) setInner(next);
    onValueChange?.(next);
    setResult(null);
  };
  const openDay = (id: string) => {
    const at = days.findIndex(d => d.id === id);
    setDirection(at >= dayIndex ? 1 : -1);
    setDayId(id);
  };

  const confirm = async () => {
    if (!onConfirm || !value || !chosenSlot || !chosenDay || chosenSlot.busy || inFlight.current || confirmed) return;
    inFlight.current = true;
    const token = ++operation.current;
    setPending(true);
    setResult(null);
    try {
      await onConfirm({ dayId: value.dayId, slotId: value.slotId, start: chosenSlot.start, ...(chosenSlot.end ? { end: chosenSlot.end } : {}) });
      if (token !== operation.current) return;
      setResult({ ok: true, message: t.confirmed(chosenDay.label, chosenSlot.label) });
    } catch {
      if (token !== operation.current) return;
      setResult({ ok: false, message: t.failed });
    } finally {
      if (token === operation.current) { inFlight.current = false; setPending(false); }
    }
  };

  const spring = reduced ? { duration: 0 } : motionTokens.spring.responsive;
  const slide = reduced
    ? { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 }, transition: { duration: 0 } }
    : { initial: { opacity: 0, x: 24 * direction }, animate: { opacity: 1, x: 0 }, exit: { opacity: 0, x: -24 * direction }, transition: motionTokens.spring.smooth };

  return <section ref={root} className={[styles.root, className].filter(Boolean).join(" ")} style={style} aria-labelledby={`${uid}-title`} data-narrow={narrow || undefined} data-tiny={tiny || undefined}>
    <header className={styles.header}>
      {!tiny && <span className={styles.icon} aria-hidden="true"><CalendarClock size={18} /></span>}
      <div className={styles.heading}>
        <h2 id={`${uid}-title`} className={styles.title}>{title}</h2>
        {(duration || timeZoneLabel) && <p className={styles.meta}>{[duration, timeZoneLabel].filter(Boolean).join(" · ")}</p>}
      </div>
      {attendees.length > 0 && <ul className={styles.attendees} aria-label={t.attendees}>
        {attendees.map(a => <li key={a.name} className={styles.attendee}>
          <Avatar name={a.name} src={a.avatar} size="sm" />
          {!narrow && <span className={styles.attendeeName}>{a.name}</span>}
        </li>)}
      </ul>}
    </header>
    <LayoutGroup id={uid}>
      <div className={styles.days} role="group" aria-label={t.days}>
        {days.map(d => {
          const on = d.id === dayId;
          return <button key={d.id} type="button" className={styles.day} aria-pressed={on} aria-label={d.label} onClick={() => openDay(d.id)}>
            {on && <motion.span layoutId={reduced ? undefined : `${uid}-day`} className={styles.dayGlide} transition={spring} aria-hidden="true" />}
            <span className={styles.weekday} aria-hidden="true">{d.weekday}</span>
            <span className={styles.dayNumber} aria-hidden="true">{d.day}</span>
            {value?.dayId === d.id && <span className={styles.dayDot} aria-hidden="true" />}
          </button>;
        })}
      </div>
      {!tiny && day && <p className={styles.dayHeader} aria-live="polite">{day.label}</p>}
      <div className={styles.slotsShell}>
        <AnimatePresence mode="popLayout" initial={false}>
          {day && <PresenceDiv key={day.id} className={styles.slots} role="group" aria-label={t.times(day.label)} {...slide}>
            {day.slots.length === 0 && <p className={styles.none}>{t.none}</p>}
            {day.slots.map(slot => {
              const on = value?.dayId === day.id && value.slotId === slot.id;
              return <button key={slot.id} type="button" className={styles.slot} aria-pressed={on} disabled={slot.busy || pending || confirmed}
                aria-label={slot.busy ? `${slot.label}, ${t.busy}` : slot.label} onClick={() => select({ dayId: day.id, slotId: slot.id })}>
                {on && <motion.span layoutId={reduced ? undefined : `${uid}-slot`} className={styles.slotGlide} transition={spring} aria-hidden="true" />}
                <span className={styles.slotLabel} aria-hidden="true">{slot.label}</span>
                {slot.busy && <span className={styles.busy} aria-hidden="true">{t.busy}</span>}
              </button>;
            })}
          </PresenceDiv>}
        </AnimatePresence>
      </div>
    </LayoutGroup>
    <footer className={styles.footer}>
      <p className={styles.summary} aria-live="polite">{chosenSlot && chosenDay ? t.selected(chosenDay.label, chosenSlot.label) : t.choose}</p>
      <Button type="button" variant="primary" disabled={!onConfirm || !chosenSlot || taken || pending || confirmed} loading={pending} onClick={() => void confirm()} aria-describedby={!onConfirm ? `${uid}-unavailable` : undefined}>
        {confirmed && <Check size={16} aria-hidden="true" />}{pending ? t.confirming : t.confirm}
      </Button>
    </footer>
    {!onConfirm && <p id={`${uid}-unavailable`} className={styles.note}>{t.unavailable}</p>}
    {taken && !confirmed && <p className={styles.error} role="alert">{t.taken}</p>}
    {result && <p className={result.ok ? styles.ok : styles.error} role={result.ok ? "status" : "alert"}>{result.message}
      {!result.ok && <button type="button" className={styles.retry} disabled={taken} onClick={() => void confirm()}>{t.retry}</button>}</p>}
  </section>;
}

export default AvailabilityPicker;
